import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export interface ReflectorOption {
  subId: string;
  name: string;
  tenantName: string;
}

interface Props {
  /** Currently selected UUID (subId). */
  value: string;
  onChange: (subId: string) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
}

async function fetchAdminReflectors(token: string): Promise<ReflectorOption[]> {
  const { data } = await axios.get<
    { subId: string; name: string; tenant?: { Name?: string; nameDisplay?: string } }[]
  >(`${API_BASE_URL}/v2/reflector-admin/instances`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return (data ?? []).map((r) => ({
    subId: r.subId ?? "",
    name: r.name ?? "",
    tenantName: r.tenant?.nameDisplay ?? r.tenant?.Name ?? "",
  }));
}

/**
 * Dropdown selector that lists ALL reflectors across every tenant (admin view).
 * Each option shows: Name — UUID (Tenant)
 */
const ReflectorSelector: React.FC<Props> = ({
  value,
  onChange,
  id = "reflector-selector",
  placeholder = "Select a reflector…",
  disabled = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;

  const [options, setOptions] = useState<ReflectorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const token = await getTokenRef.current();
      const mapped = await fetchAdminReflectors(token);
      setOptions(mapped);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function getLabel(): string {
    if (loading) return "Loading reflectors…";
    if (error) return "Error loading reflectors";
    return placeholder;
  }
  const labelText = getLabel();

  return (
    <div style={{ position: "relative" }}>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading}
        style={{
          width: "100%",
          padding: "8px 32px 8px 12px",
          border: `1px solid ${error ? "#fca5a5" : "#d1d5db"}`,
          borderRadius: 6,
          fontSize: 14,
          fontFamily: "monospace",
          background: disabled || loading ? "#f9fafb" : "#fff",
          color: value ? "#111827" : "#9ca3af",
          appearance: "none",
          cursor: disabled || loading ? "not-allowed" : "pointer",
        }}
      >
        <option value="">{labelText}</option>
        {options.map((opt) => (
          <option key={opt.subId} value={opt.subId}>
            {opt.name} — {opt.subId} ({opt.tenantName})
          </option>
        ))}
      </select>

      <span
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          color: "#6b7280",
          fontSize: 12,
        }}
      >
        ▾
      </span>

      {value && (
        <p
          style={{
            margin: "4px 0 0",
            fontSize: 11,
            fontFamily: "monospace",
            color: "#6b7280",
          }}
        >
          {value}
        </p>
      )}
    </div>
  );
};

export default ReflectorSelector;
